import React, {createContext, useContext} from 'react';
import {Form, FormikProvider, useFormik} from 'formik';
import {SHA256} from 'crypto-js';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {pathnames as P} from '../../../../common/enums';
import {getErrorMessage} from '../../../../common/utils';

export type UpdatePasswordContext = {[key: string]: never};

export type UpdatePasswordContextProviderProps = {
  children: React.ReactNode,
}

export const defaultContext: UpdatePasswordContext = {};

export type UpdatePasswordFormValues = {
  password: string,
  confirmPassword: string,
}

export const defaultFormValues: UpdatePasswordFormValues = {
  password: '',
  confirmPassword: '',
};

export const UpdatePasswordContext = createContext<UpdatePasswordContext>(defaultContext);
UpdatePasswordContext.displayName = 'UpdatePasswordContext';

export const useUpdatePasswordContext = () => useContext(UpdatePasswordContext);

export const UpdatePasswordContextProvider = ({children}: UpdatePasswordContextProviderProps) => {
  const {apiService} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();
  const E = useMessage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userID = searchParams.get('userID');

  const formik = useFormik<UpdatePasswordFormValues>({
    initialValues: {...defaultFormValues},
    validate: (values) => {
      const errors: Partial<{ [key in keyof UpdatePasswordFormValues]: string }> = {};
      if (!values.password) errors.password = E.requiredField;
      if (!values.confirmPassword) errors.confirmPassword = E.requiredField;
      if (values.password !== values.confirmPassword) errors.confirmPassword = E.textPasswordMismatch;

      return errors;
    },
    onSubmit: async (values) => {
      if (userID) {
        try {
          const encryptedPassword = SHA256(values.password);
          const result = await apiService.approvePasswordReset(
            userID,
            encryptedPassword.toString()
          );
          if (result.errorCode < 0) {
            globalMessage.open(getErrorMessage(result.errorCode, E));
          } else {
            navigate(P.LOGIN);
          }
        } catch (e) {
          globalMessage.open(E.serverError);
        }
      }
    },
    enableReinitialize: true,
  });

  const context: UpdatePasswordContext = {
    ...defaultContext,
  };

  return (
    <UpdatePasswordContext.Provider value={context}>
      <FormikProvider value={formik}>
        <Form>
          {children}
        </Form>
      </FormikProvider>
    </UpdatePasswordContext.Provider>
  );
};
